import React, { useState, useMemo, useEffect, useRef } from "react";
import { Search, ChevronLeft, ChevronRight, X, UserPlus, AlertTriangle, RefreshCw, Check, XCircle, Loader2, Pencil, Download } from "lucide-react";
import { formatDate } from "../../utils/dateFormat";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BookingsTable - Server-side paginated table for displaying bookings with search
 * Supports both Karaoke and BBQ bookings
 * Includes KJ assignment status filter and badge for unassigned bookings
 * Includes swap request status display and admin approve/reject actions
 */
const BookingsTable = ({ 
  bookings, 
  onVerifyPayment,
  onCancelPaid,
  onCancelPending,
  onChangeDate,
  onAssignKJ,
  onApproveSwap,
  onRejectSwap,
  canCancelBooking,
  facilityType = 'karaoke',
  pagination = null,
  onPageChange = null,
  onSearchChange = null,
  onKjFilterChange = null,
  onStatusFilterChange = null,
  onSortChange = null,
  isLoading = false,
  pageSize = 15
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [editingTxnBookingId, setEditingTxnBookingId] = useState(null);
  const [editingTxnValue, setEditingTxnValue] = useState("");
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [showPendingSwapsOnly, setShowPendingSwapsOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const searchTimeoutRef = useRef(null);
  
  const isKaraoke = facilityType === 'karaoke';
  const isServerSidePagination = pagination !== null && onPageChange !== null;
  
  // Count pending swap requests
  const pendingSwapCount = useMemo(() => {
    if (!isKaraoke) return 0;
    return bookings.filter(b => 
      b.swap_request_status === 'pending' && 
      b.status !== 'cancelled' && 
      b.status !== 'completed'
    ).length;
  }, [bookings, isKaraoke]);
  
  // Count urgent no KJ bookings
  const urgentNoKjCount = useMemo(() => {
    if (!isKaraoke) return 0;
    return bookings.filter(b => 
      b.is_urgent_no_kj === true && 
      b.status !== 'cancelled' && 
      b.status !== 'completed'
    ).length;
  }, [bookings, isKaraoke]);
  
  // Count unassigned KJ bookings (exclude no-KJ room bookings)
  const unassignedCount = useMemo(() => {
    if (!isKaraoke) return 0;
    return bookings.filter(b => 
      b.kj_assignment_status === 'unassigned' && 
      b.kj_required !== false &&
      b.status !== 'cancelled' && 
      b.status !== 'completed'
    ).length;
  }, [bookings, isKaraoke]);
  
  // Debounced search handler for server-side pagination
  const handleSearchInputChange = (value) => {
    setSearchInput(value);
    
    if (isServerSidePagination && onSearchChange) {
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      // Debounce search to avoid excessive API calls
      searchTimeoutRef.current = setTimeout(() => {
        onSearchChange(value);
      }, 400);
    } else {
      // Client-side: reset to page 1
      setCurrentPage(1);
    }
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);
  
  // Handle KJ filter toggle
  const handleKjFilterToggle = (filterType) => {
    if (isServerSidePagination && onKjFilterChange) {
      if (filterType === 'pending_swap') {
        const newFilter = showPendingSwapsOnly ? "" : "pending_swap";
        setShowPendingSwapsOnly(!showPendingSwapsOnly);
        setShowUnassignedOnly(false);
        onKjFilterChange(newFilter);
      } else if (filterType === 'unassigned') {
        const newFilter = showUnassignedOnly ? "" : "unassigned";
        setShowUnassignedOnly(!showUnassignedOnly);
        setShowPendingSwapsOnly(false);
        onKjFilterChange(newFilter);
      }
    } else {
      // Client-side filtering
      if (filterType === 'pending_swap') {
        setShowPendingSwapsOnly(!showPendingSwapsOnly);
        setShowUnassignedOnly(false);
      } else if (filterType === 'unassigned') {
        setShowUnassignedOnly(!showUnassignedOnly);
        setShowPendingSwapsOnly(false);
      }
      setCurrentPage(1);
    }
  };
  
  // Filter bookings (client-side filtering for legacy mode)
  const filteredBookings = useMemo(() => {
    if (isServerSidePagination) {
      // Server handles filtering, just return bookings
      return bookings;
    }
    
    let filtered = bookings;
    
    // Filter by pending swaps if enabled
    if (showPendingSwapsOnly && isKaraoke) {
      filtered = filtered.filter(b => 
        b.swap_request_status === 'pending' && 
        b.status !== 'cancelled' && 
        b.status !== 'completed'
      );
    }
    // Filter by KJ assignment status if enabled
    else if (showUnassignedOnly && isKaraoke) {
      filtered = filtered.filter(b => 
        (b.kj_assignment_status === 'unassigned' || b.is_urgent_no_kj === true) && 
        b.status !== 'cancelled' && 
        b.status !== 'completed'
      );
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }
    
    // Filter by search query
    if (searchInput.trim()) {
      const query = searchInput.toLowerCase();
      filtered = filtered.filter(b => {
        const name = isKaraoke ? b.member_name : b.name;
        const email = isKaraoke ? b.member_email : b.email;
        const code = b.booking_code;
        
        return (
          name?.toLowerCase().includes(query) ||
          email?.toLowerCase().includes(query) ||
          code?.toLowerCase().includes(query)
        );
      });
    }
    
    // Sort
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date_asc':
          return (a.date || '').localeCompare(b.date || '');
        case 'date_desc':
          return (b.date || '').localeCompare(a.date || '');
        case 'code_asc':
          return (a.booking_code || '').localeCompare(b.booking_code || '');
        case 'code_desc':
          return (b.booking_code || '').localeCompare(a.booking_code || '');
        case 'name_asc': {
          const nameA = isKaraoke ? (a.member_name || '') : (a.name || '');
          const nameB = isKaraoke ? (b.member_name || '') : (b.name || '');
          return nameA.localeCompare(nameB);
        }
        case 'name_desc': {
          const nameA = isKaraoke ? (a.member_name || '') : (a.name || '');
          const nameB = isKaraoke ? (b.member_name || '') : (b.name || '');
          return nameB.localeCompare(nameA);
        }
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [bookings, searchInput, isKaraoke, showUnassignedOnly, showPendingSwapsOnly, isServerSidePagination, statusFilter, sortBy]);
  
  // Pagination calculations
  const totalPages = isServerSidePagination 
    ? pagination.totalPages 
    : Math.ceil(filteredBookings.length / pageSize);
  const activePage = isServerSidePagination ? pagination.currentPage : currentPage;
  const startIndex = (activePage - 1) * (isServerSidePagination ? pagination.pageSize : pageSize);
  const paginatedBookings = isServerSidePagination 
    ? filteredBookings 
    : filteredBookings.slice(startIndex, startIndex + pageSize);
  const totalCount = isServerSidePagination ? pagination.totalCount : filteredBookings.length;
  const displayPageSize = isServerSidePagination ? pagination.pageSize : pageSize;
  
  // Handle page change
  const handlePageChange = (newPage) => {
    if (isServerSidePagination && onPageChange) {
      onPageChange(newPage);
    } else {
      setCurrentPage(newPage);
    }
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-gray-200 text-gray-700',
      completed_unpaid: 'bg-orange-200 text-orange-800 border border-orange-400',
      ongoing: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };
  
  const getStatusLabel = (status) => {
    if (status === 'completed_unpaid') return 'COMPLETED - UNPAID';
    return status?.toUpperCase() || 'UNKNOWN';
  };
  
  const getKJAssignmentBadge = (booking) => {
    // Urgent: KJ withdrew without replacement
    if (booking.is_urgent_no_kj) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white rounded text-xs font-bold animate-pulse">
          <AlertTriangle size={12} />
          URGENT: NO KJ
        </span>
      );
    }
    // Pending swap approval
    if (booking.swap_request_status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-400 text-amber-900 rounded text-xs font-bold">
          <RefreshCw size={12} />
          SWAP PENDING
        </span>
      );
    }
    // Standard unassigned
    if (booking.kj_assignment_status === 'unassigned') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-bold animate-pulse">
          <AlertTriangle size={12} />
          NEEDS KJ
        </span>
      );
    }
    return null;
  };

  const handleSaveTxn = async (booking) => {
    try {
      await axios.put(`${API}/admin/bookings/${booking.booking_id}/update-transaction?onepa_transaction_number=${encodeURIComponent(editingTxnValue)}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      booking.onepa_transaction_number = editingTxnValue;
      toast.success("Transaction number updated");
      setEditingTxnBookingId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by name, email or booking code..."
            value={searchInput}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border-2 border-black"
            data-testid="booking-search-input"
          />
          {isLoading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 animate-spin" size={18} />
          )}
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Pending Swaps Filter - Only for Karaoke */}
          {isKaraoke && pendingSwapCount > 0 && (
            <button
              onClick={() => handleKjFilterToggle('pending_swap')}
              className={`flex items-center gap-2 px-3 py-2 border-2 border-black text-sm font-bold transition-colors ${
                showPendingSwapsOnly 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              }`}
              data-testid="filter-pending-swaps"
            >
              <RefreshCw size={16} />
              Pending Swaps ({pendingSwapCount})
            </button>
          )}
          
          {/* KJ Unassigned Filter - Only for Karaoke */}
          {isKaraoke && (unassignedCount > 0 || urgentNoKjCount > 0) && (
            <button
              onClick={() => handleKjFilterToggle('unassigned')}
              className={`flex items-center gap-2 px-3 py-2 border-2 border-black text-sm font-bold transition-colors ${
                showUnassignedOnly 
                  ? 'bg-red-500 text-white' 
                  : urgentNoKjCount > 0 
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
              data-testid="filter-unassigned-kj"
            >
              <AlertTriangle size={16} />
              {urgentNoKjCount > 0 ? `URGENT (${urgentNoKjCount})` : `KJ Unassigned (${unassignedCount})`}
            </button>
          )}
        </div>
      </div>
      
      {/* Status Filter & Sort */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex gap-1 flex-wrap">
          {['all', 'pending', 'paid', 'completed', 'completed_unpaid', 'ongoing', 'cancelled'].map(s => (
            <button
              key={s}
              onClick={() => { 
                setStatusFilter(s); 
                setCurrentPage(1); 
                if (onStatusFilterChange) onStatusFilterChange(s);
              }}
              className={`px-2.5 py-1 text-xs font-bold border-2 transition-colors ${
                statusFilter === s
                  ? 'border-purple-500 bg-purple-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`status-filter-${s}`}
            >
              {s === 'all' ? 'All' : s === 'completed_unpaid' ? 'Unpaid' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <select
          value={sortBy}
          onChange={(e) => { 
            setSortBy(e.target.value); 
            setCurrentPage(1); 
            if (onSortChange) onSortChange(e.target.value);
          }}
          className="px-2 py-1 text-xs border-2 border-gray-300 font-bold bg-white"
          data-testid="sort-select"
        >
          <option value="date_desc">Date (Newest)</option>
          <option value="date_asc">Date (Oldest)</option>
          <option value="code_desc">Code (Z-A)</option>
          <option value="code_asc">Code (A-Z)</option>
          <option value="name_asc">Name (A-Z)</option>
          <option value="name_desc">Name (Z-A)</option>
        </select>
        <div className="text-sm text-gray-600 ml-auto">
          {isLoading ? (
            <span className="text-purple-600">Loading...</span>
          ) : (
            <>Showing {totalCount > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + displayPageSize, totalCount)} of {totalCount} bookings</>
          )}
        </div>
      </div>
      
      {/* Unassigned KJ Alert Banner */}
      {isKaraoke && unassignedCount > 0 && !showUnassignedOnly && (
        <div className="bg-red-50 border-2 border-red-400 p-3 flex items-center justify-between rounded">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-red-600" size={20} />
            <span className="font-bold text-red-800">
              {unassignedCount} booking{unassignedCount > 1 ? 's' : ''} need KJ assignment
            </span>
          </div>
          <button
            onClick={() => handleKjFilterToggle('unassigned')}
            className="text-sm font-bold text-red-700 underline hover:text-red-900"
          >
            Show All
          </button>
        </div>
      )}
      
      {/* Table */}
      {filteredBookings.length === 0 ? (
        <p className="text-gray-500 text-center py-8">
          {showPendingSwapsOnly ? 'No pending swap requests' : showUnassignedOnly ? 'No bookings needing KJ assignment' : 'No bookings found'}
        </p>
      ) : (
        <div className="overflow-x-auto border-2 border-black">
          <table className="w-full text-sm">
            <thead className="bg-purple-100">
              <tr>
                <th className="p-3 text-left text-sm font-bold">ID</th>
                <th className="p-3 text-left text-sm font-bold">{isKaraoke ? 'Resident' : 'Name'}</th>
                {isKaraoke && <th className="p-3 text-left text-sm font-bold">Contact</th>}
                {isKaraoke && <th className="p-3 text-left text-sm font-bold">KJ Assignment</th>}
                <th className="p-3 text-left text-sm font-bold">Date/Slot</th>
                <th className="p-3 text-left text-sm font-bold">Price</th>
                <th className="p-3 text-left text-sm font-bold">Status</th>
                <th className="p-3 text-left text-sm font-bold">onePA Txn#</th>
                <th className="p-3 text-left text-sm font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBookings.map(booking => (
                <tr 
                  key={booking.booking_id} 
                  className={`border-b hover:bg-purple-50 ${
                    isKaraoke && booking.kj_required !== false && booking.kj_assignment_status !== 'none_required' && booking.is_urgent_no_kj && booking.status !== 'cancelled' && booking.status !== 'completed'
                      ? 'bg-red-100 border-l-4 border-l-red-600' 
                      : isKaraoke && booking.swap_request_status === 'pending' && booking.status !== 'cancelled' && booking.status !== 'completed'
                        ? 'bg-amber-50 border-l-4 border-l-amber-500'
                        : isKaraoke && booking.kj_required !== false && booking.kj_assignment_status !== 'none_required' && booking.kj_assignment_status === 'unassigned' && booking.status !== 'cancelled' && booking.status !== 'completed'
                          ? 'bg-red-50' 
                          : ''
                  }`}
                >
                  <td className="p-3 font-mono font-bold text-purple-600 notranslate" translate="no">
                    {booking.booking_code}
                  </td>
                  <td className="p-3">
                    <div className="font-bold notranslate" translate="no">
                      {isKaraoke ? booking.member_name : booking.name}
                    </div>
                    {booking.block_unit && (
                      <div className="text-xs text-gray-400">{booking.block_unit}</div>
                    )}
                    {booking.is_special_booking && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">SPECIAL</span>
                    )}
                  </td>
                  {/* Contact Column - Mobile & Email */}
                  {isKaraoke && (
                    <td className="p-3">
                      <div className="text-xs space-y-1">
                        {booking.member_email && (
                          <div className="text-gray-600 notranslate truncate max-w-[150px]" title={booking.member_email} translate="no">
                            📧 {booking.member_email}
                          </div>
                        )}
                        {booking.member_phone && (
                          <div className="text-gray-600 notranslate" translate="no">
                            📱 {booking.member_phone}
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                  {isKaraoke && (
                    <td className="p-3">
                      {/* No KJ Required - Room Booking */}
                      {(booking.kj_assignment_status === 'none_required' || booking.kj_required === false) ? (
                        <div>
                          <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded font-bold">
                            ROOM BOOKING
                          </span>
                          <p className="text-xs text-gray-400 mt-0.5">No KJ Required</p>
                        </div>
                      ) : /* Pending Swap Request - Show swap details and approve/reject buttons */
                      booking.swap_request_status === 'pending' && booking.status !== 'cancelled' && booking.status !== 'completed' ? (
                        <div className="space-y-2">
                          {getKJAssignmentBadge(booking)}
                          <div className="text-xs space-y-1">
                            <div>
                              <span className="text-gray-500">Current:</span>{' '}
                              <span className="font-bold notranslate" translate="no">{booking.swap_requested_by_name || booking.kj_name}</span>
                            </div>
                            {booking.swap_requested_kj_name && (
                              <div>
                                <span className="text-gray-500">Replace with:</span>{' '}
                                <span className="font-bold text-green-700 notranslate" translate="no">{booking.swap_requested_kj_name}</span>
                              </div>
                            )}
                          </div>
                          {/* Approve/Reject Buttons */}
                          <div className="flex gap-1">
                            {onApproveSwap && (
                              <button
                                onClick={() => onApproveSwap(booking)}
                                className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white text-xs font-bold rounded hover:bg-green-600"
                                data-testid={`approve-swap-btn-${booking.booking_id}`}
                              >
                                <Check size={12} />
                                Approve
                              </button>
                            )}
                            {onRejectSwap && (
                              <button
                                onClick={() => onRejectSwap(booking)}
                                className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded hover:bg-red-600"
                                data-testid={`reject-swap-btn-${booking.booking_id}`}
                              >
                                <XCircle size={12} />
                                Reject
                              </button>
                            )}
                          </div>
                        </div>
                      ) : booking.is_urgent_no_kj && booking.status !== 'cancelled' ? (
                        /* Urgent: KJ Withdrew Without Replacement */
                        <div className="space-y-1">
                          {getKJAssignmentBadge(booking)}
                          <div className="text-xs text-red-700">
                            <span className="notranslate" translate="no">{booking.swap_requested_by_name}</span> withdrew
                          </div>
                          {onAssignKJ && (
                            <button
                              onClick={() => onAssignKJ(booking)}
                              className="flex items-center gap-1 px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded hover:bg-purple-600"
                              data-testid={`assign-kj-btn-${booking.booking_id}`}
                            >
                              <UserPlus size={12} />
                              Assign KJ
                            </button>
                          )}
                        </div>
                      ) : booking.kj_assignment_status === 'assigned' && booking.kj_name ? (
                        <div className="space-y-1">
                          <div className="font-bold text-purple-700 notranslate" translate="no">{booking.kj_name}</div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">ASSIGNED</span>
                            {onAssignKJ && (
                              <button
                                onClick={() => onAssignKJ(booking)}
                                className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                                title="Reassign KJ"
                                data-testid={`reassign-kj-btn-${booking.booking_id}`}
                              >
                                <Pencil size={12} />
                              </button>
                            )}
                          </div>
                          {booking.kj_was_external ? (
                            <span className="text-xs text-green-600">External KJ ($35)</span>
                          ) : (
                            <span className="text-xs text-gray-500">RN Member ($0)</span>
                          )}
                        </div>
                      ) : booking.kj_assignment_status === 'unassigned' && booking.status !== 'cancelled' ? (
                        <div className="space-y-1">
                          {getKJAssignmentBadge(booking)}
                          {onAssignKJ && (
                            <button
                              onClick={() => onAssignKJ(booking)}
                              className="flex items-center gap-1 px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded hover:bg-purple-600"
                              data-testid={`assign-kj-btn-${booking.booking_id}`}
                            >
                              <UserPlus size={12} />
                              Assign KJ
                            </button>
                          )}
                        </div>
                      ) : booking.kj_name ? (
                        <div className="space-y-1">
                          <div className="font-bold text-purple-700 notranslate" translate="no">{booking.kj_name}</div>
                          {onAssignKJ && (
                            <button
                              onClick={() => onAssignKJ(booking)}
                              className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                              title="Reassign KJ"
                              data-testid={`reassign-kj-btn-legacy-${booking.booking_id}`}
                            >
                              <Pencil size={12} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  )}
                  <td className="p-3">
                    <div className="font-mono text-sm">{formatDate(booking.date)}</div>
                    <div className="text-xs text-gray-500">
                      {booking.slot_type === 'afternoon' ? '1PM-5PM' : '6PM-10PM'}
                    </div>
                  </td>
                  <td className="p-3 font-bold">${booking.price}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-xs font-bold rounded ${getStatusBadge(booking.status)}`}>
                      {getStatusLabel(booking.status)}
                    </span>
                  </td>
                  <td className="p-3">
                    {editingTxnBookingId === booking.booking_id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={editingTxnValue}
                          onChange={(e) => setEditingTxnValue(e.target.value)}
                          className="w-28 px-2 py-1 text-xs border-2 border-purple-400 font-mono"
                          autoFocus
                          data-testid={`txn-edit-input-${booking.booking_id}`}
                        />
                        <button onClick={() => handleSaveTxn(booking)} className="p-1 bg-green-500 text-white rounded hover:bg-green-600" title="Save" data-testid={`txn-save-btn-${booking.booking_id}`}>
                          <Check size={12} />
                        </button>
                        <button onClick={() => setEditingTxnBookingId(null)} className="p-1 bg-gray-400 text-white rounded hover:bg-gray-500" title="Cancel">
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        {booking.onepa_transaction_number ? (
                          <span className="font-mono text-xs bg-green-50 text-green-700 px-2 py-1 rounded border border-green-300">
                            {booking.onepa_transaction_number}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                        {(booking.status === 'paid' || booking.status === 'completed' || booking.status === 'completed_unpaid') && (
                          <button
                            onClick={() => { setEditingTxnBookingId(booking.booking_id); setEditingTxnValue(booking.onepa_transaction_number || ''); }}
                            className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                            title="Edit Transaction Number"
                            data-testid={`txn-edit-btn-${booking.booking_id}`}
                          >
                            <Pencil size={12} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      {/* Verify Payment button - for pending or completed_unpaid */}
                      {(booking.status === 'pending' || booking.status === 'completed_unpaid') && onVerifyPayment && (
                        <button
                          onClick={() => onVerifyPayment(booking)}
                          className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600"
                          title="Verify Payment"
                        >
                          <Check size={14} />
                        </button>
                      )}
                      {booking.status === 'paid' && canCancelBooking && canCancelBooking(booking) && (
                        <button
                          onClick={() => onCancelPaid && onCancelPaid(booking)}
                          className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600"
                          title="Cancel (No Refund)"
                        >
                          <X size={14} />
                        </button>
                      )}
                      {(booking.status === 'paid' || booking.status === 'pending' || booking.status === 'completed_unpaid') && onChangeDate && (
                        <button
                          onClick={() => onChangeDate(booking)}
                          className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600"
                          title="Change Booking Date"
                          data-testid={`change-date-${booking.booking_code}`}
                        >
                          <RefreshCw size={14} />
                        </button>
                      )}
                      {booking.status === 'pending' && canCancelBooking && canCancelBooking(booking) && (
                        <button
                          onClick={() => onCancelPending && onCancelPending(booking)}
                          className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600"
                          title="Cancel Booking"
                        >
                          <X size={14} />
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/admin/bookings/${booking.booking_id}/download-receipt`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `Receipt_${booking.booking_code}.pdf`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          } catch { toast.error("Failed to download receipt"); }
                        }}
                        className="p-1.5 bg-slate-600 text-white rounded hover:bg-slate-700"
                        title="Download Receipt"
                        data-testid={`download-receipt-${booking.booking_code}`}
                      >
                        <Download size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(Math.max(1, activePage - 1))}
            disabled={activePage === 1 || isLoading}
            className={`p-2 border-2 border-black ${activePage === 1 || isLoading ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'}`}
            data-testid="pagination-prev"
          >
            <ChevronLeft size={18} />
          </button>
          
          <div className="flex gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (activePage <= 3) {
                pageNum = i + 1;
              } else if (activePage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = activePage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  disabled={isLoading}
                  className={`w-10 h-10 border-2 border-black font-bold ${
                    activePage === pageNum ? 'bg-purple-500 text-white' : 'bg-white hover:bg-purple-100'
                  } ${isLoading ? 'opacity-50' : ''}`}
                  data-testid={`pagination-page-${pageNum}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => handlePageChange(Math.min(totalPages, activePage + 1))}
            disabled={activePage === totalPages || isLoading}
            className={`p-2 border-2 border-black ${activePage === totalPages || isLoading ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'}`}
            data-testid="pagination-next"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default BookingsTable;
